import type { ProjectTypeConfig } from "@/data/models/ProjectDetails";

/**
 * The client's own "Mögliche Projekttypen" table under the project types IRES gives it (#2610081).
 * Pure; a type-only import, so the specs do not pull the project model.
 *
 * IRES Project, IRES Work and the interfaces read the stored table as it is, and IRES Work lays it over
 * its own defaults by type id: an entry missing from the table comes back with Work's default, which is
 * active for some types. So a save never drops an entry, and a type IRES withheld is stored switched off.
 */

/** Whether Einstellungen Projektbearbeitung shows a type: every type without a list, else the listed ones. */
export function isProjectTypeShown(projectTypeId: number, allowList: number[] | null | undefined): boolean {
  return !Array.isArray(allowList) || allowList.includes(projectTypeId);
}

/**
 * The table to store after a save on Einstellungen Projektbearbeitung: the page's rows (the edited
 * shown ones, a withheld one switched off), then every stored entry the page has no row for, unchanged,
 * so no entry is ever dropped.
 *
 * @param rows - the page's rows, shown and hidden.
 * @param stored - the table as stored, or absent.
 * @param allowList - the types IRES gives the client, or absent for every type.
 * @returns the table to store; the inputs are not changed.
 */
export function projectTypesForSave(
  rows: ProjectTypeConfig[],
  stored: ProjectTypeConfig[] | null | undefined,
  allowList: number[] | null | undefined
): ProjectTypeConfig[] {
  const saved = rows.map((row) => (isProjectTypeShown(row.projectTypeId, allowList) ? row : { ...row, active: false }));
  const rowIds = new Set(rows.map((row) => row.projectTypeId));
  const kept = (Array.isArray(stored) ? stored : []).filter((entry) => !rowIds.has(entry?.projectTypeId));
  return [...saved, ...kept];
}
